import React, {
  FC, useCallback, useContext, useMemo,
} from 'react';
import { FormikHelpers, useFormik } from 'formik';

import Button from 'components/library/Button';
import { TUser } from 'types';
import { IProfileIntegrationTabFormBind } from 'types/Integration';
import strings from 'constants/localization';
import AlertContext from 'contexts/ContextAlert';
import UserContext from 'contexts/ContextUser';
import { useCreateIntegrationEventBind, useDeleteIntegrationEventBind } from 'hooks/Integration/useIntegrationEventBind';
import { nanoid } from 'nanoid';
import containerCss from '../Tab.module.css';
import EventBindInformation from '../EventBindInformation';

const EVENT = 'event';

interface IIntegrationConnect {
  customerId: string,
  userName: string,
  eventBind: TUser['eventBind']
}

const ManualIntegrationEventBind: FC<IIntegrationConnect> = ({ customerId, userName, eventBind }) => {
  const {
    id: eventBindId,
    eventId: bindedEventId,
    eventName: bindedEventName,
    connection
  } = eventBind || {};

  const { user } = useContext(UserContext);
  const { push } = useContext(AlertContext);

  const connectionId = useMemo<string | null | undefined>(
    () => user?.integration?.id,
    [user]
  );

  const { mutateAsync: createEventBind } = useCreateIntegrationEventBind({ customerId, connectionId });
  const { mutateAsync: deleteEventBind, isLoading: isDeleting } = useDeleteIntegrationEventBind({ customerId, connectionId });

  const onBindEvent = async (formData: IProfileIntegrationTabFormBind, options: FormikHelpers<IProfileIntegrationTabFormBind>) => {
    const { event } = formData;
    if (event) {
      options.setSubmitting(true);

      const { label: newEventName, value: newEventId } = event;
      const commonSaveData = { eventId: newEventId, eventName: newEventName, organizationId: null };

      await createEventBind(commonSaveData)
        .then(() => {
          push({ severity: 'success', message: eventBindId ? strings.successEventBindUpdate : strings.successEventBind });
        })
        .catch(err => {
          const errMsg = err?.response?.data?.data?.reason || err?.response?.data?.message;
          push({ severity: 'error', message: errMsg });
        })
        .finally(() => {
          options.setSubmitting(false);
        });
    }
  };

  const {
    values: { event: chosenEvent },
    isSubmitting,
    resetForm,
    handleSubmit,
  } = useFormik({
    initialValues: {
      [EVENT]: { label: userName, value: nanoid() },
    } as IProfileIntegrationTabFormBind,
    onSubmit: onBindEvent,
  });

  const onDeleteEvent = useCallback(
    () => {
      const { id } = eventBind || {};
      if (id) {
        deleteEventBind(id)
          .then(() => {
            resetForm();
            push({ severity: 'success', message: strings.successEventBindDelete });
          })
          .catch(err => {
            const errMsg = err?.response?.data?.data?.reason || err?.response?.data?.message;
            push({ severity: 'error', message: errMsg });
          });
      }
    },
    [eventBind, deleteEventBind]
  );

  const submitBtnDisabled = useMemo<boolean>(
    () => isSubmitting || isDeleting || !chosenEvent?.value || !chosenEvent?.label || !!bindedEventId,
    [isSubmitting, chosenEvent, isDeleting]
  );

  return (
    <>
      {bindedEventName && (
        <EventBindInformation
          bindedEventName={bindedEventName}
          connectedService={connection?.service}
        />
      )}
      <form noValidate onSubmit={handleSubmit}>
        <div>
          <Button
            type="submit"
            buttonType="primary"
            disabled={submitBtnDisabled}
          >{strings.bindEvent}</Button>
          <Button
            type="button"
            buttonType="tertiary"
            className={containerCss.deleteBtn}
            disabled={!bindedEventId}
            onClick={onDeleteEvent}
          >{strings.actionLabelRemoveEventBinding}</Button>
        </div>
      </form>
    </>
  );
};

export default ManualIntegrationEventBind;
